import {
  Pipe,
  PipeTransform,
  OnDestroy,
  ChangeDetectorRef,
  ApplicationRef,
} from '@angular/core';
import { TranslationService } from '../appServices/translation.service';
import { Subscription, combineLatest } from 'rxjs';

@Pipe({
  name: 'translate',
  pure: false, // ✅ Much better performance
  standalone: false,
})
export class TranslationPipe implements PipeTransform, OnDestroy {
  private static translationCache = new Map<string, string>();
  private static currentLanguage = '';
  private static languageChangeCount = 0;
  private subscription: Subscription | null = null;
  private lastLanguageChangeCount = 0;

  private tickScheduled = false;

  constructor(
    private translationService: TranslationService,
    private appRef: ApplicationRef
  ) {
    // Subscribe to both language changes and translation loading events
    // This ensures re-render when translations finish loading after app start/refresh
    this.subscription = combineLatest([
      this.translationService.currentLanguage$,
      this.translationService.translationsLoaded$
    ]).subscribe(([lang, loaded]) => {
      let shouldUpdate = false;
      
      // Update language if changed
        if (TranslationPipe.currentLanguage !== lang) {
          TranslationPipe.currentLanguage = lang;
          TranslationPipe.languageChangeCount++;
          TranslationPipe.translationCache.clear();
        shouldUpdate = true;
      }
      
      // When translations are loaded, trigger re-render
      if (loaded) {
        TranslationPipe.translationCache.clear();
        TranslationPipe.languageChangeCount++;
        shouldUpdate = true;
      }
      
      // Schedule change detection asynchronously to prevent recursive calls
      if (shouldUpdate && !this.tickScheduled) {
        this.tickScheduled = true;
        setTimeout(() => {
          this.tickScheduled = false;
          // Use requestAnimationFrame for smoother updates
          requestAnimationFrame(() => {
          this.appRef.tick();
          });
        }, 0);
      }
    });
  }

  transform(key: string, params?: { [key: string]: string | number }): string {
    // Force re-evaluation when language changes
    const currentChangeCount = TranslationPipe.languageChangeCount;
    const currentLang = this.translationService.getCurrentLanguage();
    const cacheKey = `${currentLang}:${key}:${JSON.stringify(
      params || {}
    )}:${currentChangeCount}`;

    // Check cache first (includes language change count for cache invalidation)
    if (
      this.lastLanguageChangeCount === currentChangeCount &&
      TranslationPipe.translationCache.has(cacheKey)
    ) {
      return TranslationPipe.translationCache.get(cacheKey)!;
    }

    this.lastLanguageChangeCount = currentChangeCount;

    // Get translation and cache it
    const translation = this.translationService.translate(key, params);

    // Manage cache size (LRU-style)
    if (TranslationPipe.translationCache.size > 500) {
      const firstKey = TranslationPipe.translationCache.keys().next().value;
      if (firstKey) {
        TranslationPipe.translationCache.delete(firstKey);
      }
    }

    TranslationPipe.translationCache.set(cacheKey, translation);

    return translation;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  // Static method to clear cache if needed
  static clearCache(): void {
    TranslationPipe.translationCache.clear();
    TranslationPipe.languageChangeCount++;
  }
}
